import React, { useEffect, useRef, useState } from 'react';
import styles from '../NDAGenerator.module.scss';

export interface ChatMessageItem {
  id: string;
  type: 'assistant' | 'user';
  text: string;
  time: string;
}

interface ChatPanelProps {
  messages: ChatMessageItem[];
  loading: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onSend: () => void;
  placeholder?: string;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  loading,
  inputRef,
  onSend,
  placeholder
}) => {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);

  useEffect(() => {
    if (!isNearBottom) return;
    const el = containerRef.current;
    if (!el) return;

    el.scrollTop = el.scrollHeight;
  }, [messages, loading, isNearBottom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    setIsNearBottom(true);
  }, []);

  useEffect(() => {
    const input = inputRef?.current;
    if (input) {
      input.focus();

      try {
        const len = input.value?.length ?? 0;
        input.setSelectionRange(len, len);
      } catch {
        // ignore if selection range isn't supported
      }
    }
  }, []);

  const updateNearBottom = (): void => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsNearBottom(distanceFromBottom < 60);
  };

  useEffect(() => {
    updateNearBottom();
  }, [messages]);

  useEffect(() => {
    if (!isNearBottom || messages.length === 0) return;
    const last = messages.at(-1);
    if (last?.type === 'assistant') {
      const input = inputRef?.current;
      if (input) {
        input.focus();
        try {
          const len = input.value?.length ?? 0;
          input.setSelectionRange(len, len);
        } catch {
          // ignore if selection range isn't supported
        }
      }
    }
  }, [messages, isNearBottom]);

  return (
    <div className={styles.rightPanel}>
      <div className={styles.chatContainer}>
        <div className={styles.chatHeader}>
          <h3>NDA Assistant</h3>
        </div>
        <div
          className={styles.chatMessages}
          ref={containerRef}
          onScroll={updateNearBottom}
        >
          {messages.map((m) => (
            <div
              key={m.id}
              className={`${styles.messageWrapper} ${
                m.type === 'assistant'
                  ? styles.assistantWrapper
                  : styles.userWrapper
              }`}
            >
              <div
                className={`${styles.chatBubble} ${
                  m.type === 'assistant' ? styles.assistant : styles.user
                }`}
              >
                <div className={styles.messageContent}>{m.text}</div>
                <div className={styles.messageTime}>{m.time}</div>
              </div>
            </div>
          ))}
          {loading && (
            <div
              className={`${styles.messageWrapper} ${styles.assistantWrapper}`}
            >
              <div className={`${styles.chatBubble} ${styles.assistant}`}>
                <div className={styles.messageContent}>Thinking…</div>
                <div className={styles.messageTime}>
                  {new Date().toLocaleTimeString()}
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
        <div className={styles.chatInput}>
          {!isNearBottom && (
            <button
              type="button"
              className={styles.sendButton}
              onClick={() => {
                const el = containerRef.current;
                if (el) el.scrollTop = el.scrollHeight;
                setIsNearBottom(true);
              }}
            >
              Scroll to latest
            </button>
          )}
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            placeholder={
              placeholder ??
              "Ask the assistant or type details (e.g., 'Counterparty is ABC Corp')"
            }
            disabled={loading}
          />
          <button
            type="button"
            className={styles.sendButton}
            onClick={onSend}
            disabled={loading}
          >
            {loading ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};
